import { useMemo, useRef, useState } from "react";
import { useQueries } from "@tanstack/react-query";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Search, MessagesSquare, Users2, Hash, Ban } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { accountOfflineReason, chatOutgoingStatus, chatPreview, cn, fmtTime, initials, colorFromString, pendingPreviewText } from "@/lib/utils";
import { Avatar } from "@/components/ui/avatar";
import { OutgoingStatusIcon } from "@/components/ChatList";
import { useWorkspace } from "@/store/workspace";
import { useOutbox } from "@/store/outbox";
import type { Account, Chat } from "@/lib/types";

interface FlatChat extends Chat {
  account: Account;
}

export function UnifiedChatList({ accounts }: { accounts: Account[] }) {
  const { selectedAccountId, selectedChatId, selectChatWithAccount } = useWorkspace();
  const [q, setQ] = useState("");

  const queries = useQueries({
    queries: accounts.map((a) => ({
      queryKey: ["chats", a.id],
      queryFn: () => api.listChats(a.id),
      refetchInterval: 60_000,
    })),
  });

  const flat: FlatChat[] = useMemo(() => {
    const out: FlatChat[] = [];
    accounts.forEach((a, idx) => {
      const data = (queries[idx]?.data ?? []) as Chat[];
      for (const c of data) out.push({ ...c, account: a });
    });
    out.sort((a, b) => {
      const ta = a.last_message_at ? new Date(a.last_message_at).getTime() : 0;
      const tb = b.last_message_at ? new Date(b.last_message_at).getTime() : 0;
      return tb - ta;
    });
    return out;
  }, [accounts, queries]);

  const filtered = useMemo(() => {
    let res = flat;
    const t = q.trim().toLowerCase();
    if (t) {
      res = res.filter((c) => {
        const accName = c.account.username || [c.account.first_name, c.account.last_name].filter(Boolean).join(" ") || c.account.phone;
        return (
          (c.title ?? "").toLowerCase().includes(t) ||
          (c.username ?? "").toLowerCase().includes(t) ||
          accName.toLowerCase().includes(t) ||
          c.account.phone.includes(t)
        );
      });
    }
    return res;
  }, [flat, q]);

  // Здесь собраны диалоги ВСЕХ аккаунтов — счёт идёт на тысячи строк,
  // без виртуализации список ощутимо тормозит уже на паре сотен.
  const listRef = useRef<HTMLDivElement>(null);
  const rowVirtualizer = useVirtualizer({
    count: filtered.length,
    getScrollElement: () => listRef.current,
    estimateSize: () => 61,
    getItemKey: (i) => {
      const c = filtered[i];
      return c ? `${c.account.id}:${c.id}` : i;
    },
    overscan: 10,
  });

  const anyLoading = queries.some((q) => q.isFetching) && flat.length === 0;
  // Хоть у одного живого аккаунта список диалогов ещё не приезжал из Telegram.
  const dialogsPending = accounts.some((a) => !a.dialogs_synced_at && !accountOfflineReason(a));

  return (
    <div className="flex w-96 shrink-0 flex-col border-r border-border bg-card/20">
      <div className="border-b border-border p-3">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold">Все диалоги</h2>
          <div className="flex items-center gap-0.5">
            <span className="mr-1 text-[11px] text-muted-foreground">{flat.length}</span>
          </div>
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Поиск чата / аккаунта" className="pl-8 h-9" />
        </div>
      </div>

      <div ref={listRef} className="flex-1 overflow-y-auto">
        {anyLoading ? (
          <div className="p-6 text-center text-sm text-muted-foreground">Загрузка…</div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center gap-2 p-8 text-center text-sm text-muted-foreground">
            <MessagesSquare className="h-8 w-8 opacity-30" />
            {q.trim() && flat.length > 0
              ? "Ничего не найдено по запросу."
              : dialogsPending
                ? "Загружаем список диалогов из Telegram…"
                : "Диалогов нет"}
          </div>
        ) : (
          <ul className="relative w-full" style={{ height: rowVirtualizer.getTotalSize() }}>
            {rowVirtualizer.getVirtualItems().map((v) => {
              const c = filtered[v.index];
              return (
                <UnifiedChatRow
                  key={v.key}
                  index={v.index}
                  offset={v.start}
                  measure={rowVirtualizer.measureElement}
                  chat={c}
                  active={c.id === selectedChatId && c.account.id === selectedAccountId}
                  onClick={() => selectChatWithAccount(c.account.id, c.id)}
                />
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}

function UnifiedChatRow({
  chat,
  active,
  onClick,
  index,
  offset,
  measure,
}: {
  chat: FlatChat;
  active: boolean;
  onClick: () => void;
  /** Позиция в виртуализированном списке: строка абсолютная, высота измеряется. */
  index: number;
  offset: number;
  measure: (el: HTMLElement | null) => void;
}) {
  const title = chat.title || (chat.username ? `@${chat.username}` : `peer ${chat.tg_peer_id}`);
  const icon =
    chat.kind === "private" ? null : chat.kind === "group" ? <Users2 className="h-3 w-3" /> : <Hash className="h-3 w-3" />;
  const accName = chat.account.username
    ? `@${chat.account.username}`
    : [chat.account.first_name, chat.account.last_name].filter(Boolean).join(" ") || chat.account.phone;
  const accColor = colorFromString(chat.account.id);
  const outboxItems = useOutbox((s) => s.items);
  const pending = useMemo(
    () => outboxItems.filter((i) => i.chatId === chat.id),
    [outboxItems, chat.id]
  );
  const status = chatOutgoingStatus(chat, pending);
  const preview = chatPreview(chat, pending[0] ? pendingPreviewText(pending[0]) : null);

  return (
    <li
      data-index={index}
      ref={measure}
      className="absolute left-0 top-0 w-full"
      style={{ transform: `translateY(${offset}px)` }}
    >
      <button
        onClick={onClick}
        className={cn(
          "flex w-full items-center gap-3 border-l-2 border-transparent px-3 py-2.5 text-left transition-colors hover:bg-accent/60",
          active && "bg-accent text-foreground"
        )}
      >
        <Avatar
          name={title}
          initials={initials(title.split(" ")[0], title.split(" ")[1], "?")}
          size={36}
          src={chat.avatar_media_id ? api.mediaUrl(chat.avatar_media_id) : null}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className="flex items-center gap-1 truncate text-sm font-medium">
              {icon}
              {title}
              {chat.peer_blocked_at && (
                <span
                  className="flex h-5 shrink-0 items-center gap-1 rounded bg-muted px-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground"
                  title={
                    chat.peer_blocked_reason === "deactivated"
                      ? "Собеседник удалил аккаунт — доставка невозможна"
                      : "Собеседник заблокировал аккаунт — доставка невозможна"
                  }
                >
                  <Ban className="h-3 w-3" />
                  {chat.peer_blocked_reason === "deactivated" ? "Удалён" : "Заблок."}
                </span>
              )}
            </span>
            <span className="shrink-0 text-[11px] text-muted-foreground">{fmtTime(chat.last_message_at)}</span>
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="flex min-w-0 flex-1 items-baseline gap-1.5">
              <span
                className="shrink-0 text-[11px]"
                style={{ color: accColor }}
                title={chat.account.phone}
              >
                {accName}
              </span>
              <span
                className={cn(
                  "flex min-w-0 items-center gap-1 truncate text-xs text-muted-foreground",
                  chat.unread_count > 0 && "text-foreground/80"
                )}
              >
                <OutgoingStatusIcon status={status} />
                <span
                  className={cn(
                    "truncate",
                    // Служебное — событие, а не чья-то реплика: другим цветом,
                    // как в официальном клиенте.
                    chat.last_message?.service && pending.length === 0 && "italic text-primary/80"
                  )}
                >
                  {preview}
                </span>
              </span>
            </span>
            {chat.unread_count > 0 && (
              <span className="ml-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-semibold text-primary-foreground">
                {chat.unread_count}
              </span>
            )}
          </div>
        </div>
      </button>
    </li>
  );
}
